import { Loader2, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Badge, Button } from 'slab'
import { getFormattedDate } from 'utils'
import { useGetBrokersTasksTaskId } from 'utils/web_api'
import { type BrokersTask } from 'utils/web_api.schemas'

import TaskActionControls from './TaskActionControls'
import {
  AdminAuditHistorySection,
  ExecutionHistorySection,
  PlaywrightExecutionSection,
  RunbookInstructionsSection,
  TaskActionsSection,
  TaskMetadataSection,
  TaskNotesSection,
} from './TaskAdminSections'
import { useTaskAdminActions } from './useTaskAdminActions'
import { useTaskMetadata } from './useTaskMetadata'
import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  TaskStatusBadge,
} from './WorkflowVisualIndicators'

type TaskDetailsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  taskId: string | null
  workflowId: string
  enrollmentId: string
  handleTaskAction: (taskId: string, action: string) => void
  taskActionLoading: boolean
}

const POLLING_INTERVAL_MS = 3000

const TaskDetailsDialog = ({
  open,
  onOpenChange,
  taskId,
  workflowId,
  enrollmentId,
  handleTaskAction,
  taskActionLoading,
}: TaskDetailsDialogProps) => {
  const enabled = open && Boolean(taskId)
  const taskIdOrFallback = taskId ?? ''

  const taskQuery = useGetBrokersTasksTaskId(taskIdOrFallback, {
    query: {
      enabled,
      refetchInterval: (data) => {
        if (!enabled) return false
        const isProcessing = data.state.data?.status && data.state.data.status === 'processing'
        return isProcessing ? POLLING_INTERVAL_MS : false
      },
      refetchIntervalInBackground: false,
    },
  })
  const taskDetail = taskQuery.data
  const taskLoading = taskQuery.isLoading

  const adminActions = useTaskAdminActions(taskId, workflowId || null, enabled)
  const metadata = useTaskMetadata(taskDetail)

  const [auditSectionOpen, setAuditSectionOpen] = useState(false)

  const handleManualRefresh = async () => {
    await Promise.all([taskQuery.refetch(), adminActions.taskResultsQuery.refetch()])
  }

  const isRefreshing = taskQuery.isFetching || adminActions.taskResultsQuery.isFetching

  useEffect(() => {
    if (!open) {
      adminActions.setIncludeSafety(true)
      adminActions.setIgnoreHold(false)
      setAuditSectionOpen(false)
    }
  }, [open, adminActions])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden p-0'>
        <DialogHeader className='border-b border-gray-100 px-6 py-4'>
          <DialogTitle className='text-lg'>
            {taskDetail?.display_name ?? 'Task details'}
          </DialogTitle>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {taskLoading && (
            <div className='flex flex-1 items-center justify-center py-16'>
              <Loader2 className='h-6 w-6 animate-spin text-gray-400' />
            </div>
          )}

          {!taskLoading && !taskDetail && (
            <div className='flex flex-1 items-center justify-center p-6 text-sm text-gray-600'>
              Unable to load task details.
            </div>
          )}

          {!taskLoading && taskDetail && (
            <div className='flex-1 overflow-y-auto px-6 py-6'>
              <div className='space-y-6'>
                <section className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
                  <div className='flex flex-wrap items-start justify-between gap-4'>
                    <div>
                      <div className='flex flex-wrap items-center gap-3'>
                        <TaskStatusBadge status={taskDetail.status} isExecutable={taskDetail.can_execute} />
                        <ExecutionMethodBadge method={taskDetail.execution_method} />
                        <ExecutionContextBadge context={taskDetail.execution_context} />
                        <Badge type='neutral'>
                          {taskDetail.id.slice(-6)}
                        </Badge>
                        <Button
                          type='button'
                          variant='tertiary'
                          size='small'
                          onClick={handleManualRefresh}
                          disabled={isRefreshing}
                        >
                          <RotateCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      <p className='mt-2 text-sm text-gray-600'>
                        {taskDetail.description}
                      </p>
                    </div>
                    <div className='flex flex-wrap justify-end gap-2'>
                      <TaskActionControls
                        task={taskDetail as unknown as BrokersTask}
                        onTaskAction={handleTaskAction}
                        isLoading={taskActionLoading}
                        workflowId={workflowId}
                        enrollmentId={enrollmentId}
                        withPortal={false}
                      />
                    </div>
                  </div>

                  <div className='mt-4 grid gap-4 md:grid-cols-2'>
                    <div className='text-sm text-gray-600'>
                      <span className='font-medium text-gray-800'>Created</span>{' '}
                      {getFormattedDate(taskDetail.created_at, { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    <div className='text-sm text-gray-600'>
                      <span className='font-medium text-gray-800'>Updated</span>{' '}
                      {getFormattedDate(taskDetail.updated_at, { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    {taskDetail.message && (
                      <div className='md:col-span-2 rounded border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700'>
                        {taskDetail.message}
                      </div>
                    )}
                  </div>

                  <div className='mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-600'>
                    <span>Failures: {taskDetail.fail_count}</span>
                    <span>Sequence: {taskDetail.sequence_order ?? '—'}</span>
                  </div>
                </section>

                <PlaywrightExecutionSection
                  task={taskDetail}
                  taskId={taskId!}
                  adminActions={adminActions}
                />

                <TaskActionsSection task={taskDetail} adminActions={adminActions} />

                <RunbookInstructionsSection metadata={metadata} />

                <TaskMetadataSection task={taskDetail} />

                <ExecutionHistorySection
                  taskId={taskId!}
                  adminActions={adminActions}
                />

                <TaskNotesSection task={taskDetail} />

                <AdminAuditHistorySection
                  task={taskDetail}
                  auditSectionOpen={auditSectionOpen}
                  setAuditSectionOpen={setAuditSectionOpen}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TaskDetailsDialog
